import React, { useEffect, useState, useCallback } from 'react';
import { Table, Tag, Progress, Button, Modal, Switch, Checkbox, message as antMessage } from 'antd';
import { CloseCircleOutlined, ExclamationCircleOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchChargesForSetting } from '../../../services/chargesApi';
import api from '../../../services/api';
import InvoicesModal from './InvoicesModal';

const HistoryTab = ({ settingId, refreshToken }) => {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCanceled, setShowCanceled] = useState(false);
  const [invoicesOpen, setInvoicesOpen] = useState(false);
  const [selectedChargeId, setSelectedChargeId] = useState(null);

  const openInvoices = (chargeId) => {
    setSelectedChargeId(chargeId);
    setInvoicesOpen(true);
  };

  const load = useCallback(async () => {
    if (!settingId) return;
    setLoading(true);
    try {
      const data = await fetchChargesForSetting(settingId, {
        page,
        limit: pageSize,
        includeCanceled: showCanceled ? '1' : '0',
      });
      setItems(data.charges || []);
      setTotal(data.total || 0);
    } catch {
      antMessage.error('Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingId, page, pageSize, showCanceled, refreshToken]);

  useEffect(() => { load(); }, [load]);

  const handleToggleCanceled = (checked) => {
    setShowCanceled(checked);
    setPage(1);
  };

  const getStatusLabelPtBr = (status) => {
    const s = String(status || '').toUpperCase();
    if (s === 'ACTIVE') return 'Ativo';
    if (s === 'INACTIVE') return 'Inativo';
    if (s === 'PENDING') return 'Pendente';
    if (s === 'PROCESSED') return 'Processado';
    if (s === 'CANCELED') return 'Cancelado';
    return 'Desconhecido';
  };

  const handleCancel = (record) => {
    let keepPendingInvoices = true; // checkbox padrão marcado

    Modal.confirm({
      title: 'Confirmar Cancelamento da Cobrança',
      icon: <ExclamationCircleOutlined />,
      width: 560,
      content: (
        <div style={{ display: 'grid', gap: 14 }}>
          <div
            style={{
              display: 'grid',
              gap: 10,
              padding: '14px 16px',
              borderRadius: 12,
              background: '#fafafa',
              border: '1px solid #f0f0f0',
              lineHeight: 1.45,
            }}
          >
            <div><strong>Descrição:</strong> {record.charge_description || '-'}</div>
            <div>
              <strong>Data Emissão:</strong>{' '}
              {record.charge_issue_date ? dayjs(record.charge_issue_date).format('DD/MM/YYYY') : '-'}
            </div>
            <div><strong>Status Atual:</strong> {getStatusLabelPtBr(record.status)}</div>
          </div>

          <div
            style={{
              padding: '12px 14px',
              borderRadius: 12,
              background: '#fff7e6',
              border: '1px solid #ffd591',
              color: '#d48806',
              lineHeight: 1.5,
            }}
          >
            ⚠️ Atenção: ao cancelar uma cobrança, todos os jogadores deixarão automaticamente de receber novas cobranças.
          </div>

          <div
            style={{
              padding: '14px 16px',
              borderRadius: 12,
              border: '1px solid #e6f4ff',
              background: '#f5fbff',
            }}
          >
            <Checkbox
              defaultChecked
              onChange={(e) => { keepPendingInvoices = e.target.checked; }}
            >
              Manter faturas pendentes
            </Checkbox>
            <div style={{ marginTop: 8, color: '#4f5b66', fontSize: 11, lineHeight: 1.45 }}>
              <b>Marcado</b>: a cobrança será cancelada, mas as faturas pendentes continuarão ativas.<br />
              <b>Desmarcado</b>: a cobrança e as faturas pendentes serão canceladas.
            </div>
          </div>

          <div style={{ lineHeight: 1.45 }}>Deseja continuar?</div>
        </div>
      ),
      okText: 'Sim, Cancelar Cobrança',
      okButtonProps: { danger: true },
      cancelText: 'Voltar',
      onOk: async () => {
        try {
          const payload = keepPendingInvoices ? {} : { cancel_pending_invoices: true };
          await api.put(`/organizer-charges/charges/${record.id}/cancel`, payload);
          antMessage.success('Cobrança cancelada com sucesso');
          load();
        } catch (error) {
          antMessage.error(error?.response?.data?.message || 'Erro ao cancelar cobrança');
        }
      },
    });
  };

  const statusTag = (status) => {
    const map = {
      PENDING: { color: 'orange', label: 'Pendente' },
      PROCESSED: { color: 'green', label: 'Processado' },
      CANCELED: { color: 'red', label: 'Cancelado' },
      ERROR: { color: 'red', label: 'Erro' },
    };
    const m = map[status] || { color: 'default', label: status };
    return <Tag color={m.color}>{m.label}</Tag>;
  };

  const columns = [
    {
      title: 'Mês',
      dataIndex: 'reference_month',
      width: 100,
      render: (v) => v ? <strong>{v}</strong> : '-',
    },
    {
      title: 'Dia de Cobrança',
      dataIndex: 'charge_issue_day',
      width: 130,
      align: 'center',
      render: (v) => v != null ? String(v).padStart(2, '0') : '-',
    },
    {
      title: 'Cobrado em',
      dataIndex: 'charge_process_date',
      width: 170,
      render: (v) => v
        ? dayjs(v).format('DD/MM/YYYY HH:mm')
        : <Tag color="blue">Aguardando</Tag>,
    },
    {
      title: 'Pagas / Total',
      width: 110,
      align: 'center',
      render: (_, r) => `${r.paid_invoices}/${r.total_invoices}`,
    },
    {
      title: 'Cobrado / Recebido',
      width: 180,
      align: 'right',
      render: (_, r) => (
        <span>
          R$ {(r.total_amount || 0).toFixed(2)} / <strong>R$ {(r.received_amount || 0).toFixed(2)}</strong>
        </span>
      ),
    },
    {
      title: 'Progresso',
      width: 140,
      render: (_, r) => {
        const pct = r.total_amount > 0 ? Math.round((r.received_amount / r.total_amount) * 100) : 0;
        return <Progress percent={pct} size="small" />;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: statusTag,
    },
    {
      title: 'Ações',
      width: 220,
      render: (_, r) => (
        <div style={{ display: 'flex', gap: 4 }}>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => openInvoices(r.id)}
          >
            Ver Faturas
          </Button>
          {r.status !== 'CANCELED' && (
            <Button
              type="link"
              danger
              size="small"
              icon={<CloseCircleOutlined />}
              onClick={() => handleCancel(r)}
            >
              Cancelar
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ color: '#8c8c8c' }}>Mostrar canceladas</span>
        <Switch checked={showCanceled} onChange={handleToggleCanceled} />
      </div>
      <Table
        rowKey="id"
        loading={loading}
        dataSource={items}
        columns={columns}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: (p) => setPage(p),
          showSizeChanger: false,
        }}
        scroll={{ x: 'max-content' }}
        size="small"
      />
      <InvoicesModal
        open={invoicesOpen}
        chargeId={selectedChargeId}
        onClose={() => setInvoicesOpen(false)}
      />
    </>
  );
};

export default HistoryTab;
